import { NgModule } from "@angular/core";
import { HttpHeaders } from "@angular/common/http";

import { ApolloModule, APOLLO_OPTIONS, APOLLO_NAMED_OPTIONS, NamedOptions } from "apollo-angular";
import { ApolloClientOptions, ApolloLink } from "@apollo/client/core";
import { InMemoryCache, TypePolicies } from "@apollo/client/cache";
import { HttpLink } from "apollo-angular/http";
import { onError } from "@apollo/client/link/error";

import { config } from "@config";
import { UnauthorizedError } from "@models";
import { Helper } from "@helpers/functions";
import { ACCESS_TOKEN_KEY } from "@helpers/constants";
import { TranslationService } from "@core-services/translation.service";
import { NormalizedCacheObject } from "@apollo/client/cache/inmemory/types";

const { apiUrlGql, apiUrlGqlStaking, apiUrlGqlBff } = config; // <-- add the URL of the HTTP GraphQL server here

const getHeaderLanguage = (translationService: TranslationService): string => {
	let lang = translationService.getLanguage();
	if ("" + lang === "null") {
		lang = "de";
	}

	return lang;
};

const createHttpLink = (httpLink: HttpLink, translationService: TranslationService, uri: string): ApolloLink => {
	const http = httpLink.create({ uri });
	const middleware = createHttpMiddleware(translationService);
	const errorAfterware = createErrorAfterware();
	return errorAfterware.concat(middleware.concat(http));
};

// Http middleware --> e.g. to append headers
const createHttpMiddleware = (translationService: TranslationService): ApolloLink =>
	new ApolloLink((operation, forward) => {
		operation.setContext({
			headers: new HttpHeaders({
				"access-token": Helper.getCookie(ACCESS_TOKEN_KEY),
				"accept-language": getHeaderLanguage(translationService)
			})
		});
		return forward(operation);
	});

const createBffHttpLink = (httpLink: HttpLink, translationService: TranslationService, uri: string): ApolloLink => {
	const http = httpLink.create({ uri });
	const middleware = createBffHttpMiddleware(translationService);
	const errorAfterware = createErrorAfterware();
	return errorAfterware.concat(middleware.concat(http));
};

const createBffHttpMiddleware = (translationService: TranslationService): ApolloLink =>
	new ApolloLink((operation, forward) => {
		operation.setContext({
			headers: new HttpHeaders({
				"access-token": Helper.getCookie(ACCESS_TOKEN_KEY),
				"accept-language": getHeaderLanguage(translationService),
				"x-currency": Helper.getCookie("user-currency")
			})
		});
		return forward(operation);
	});

/**
 * NetworkErrors are caught in the error-interceptor.service and rethrown,
 * so networkError is actually an instance of our domain error --> ErrorClass | Error
 */
const createErrorAfterware = (): ApolloLink =>
	onError(({ networkError }) => {
		// As of now, all 401 coming from GQL should end the current session
		if (networkError instanceof UnauthorizedError) {
			window.location.href = "/user/login";
		}
	});

const typePolicies: TypePolicies = {
	Query: {
		fields: {
			settings: {
				merge(existing: Record<string, unknown> = {}, incoming: Record<string, unknown>) {
					return { ...existing, ...incoming };
				}
			}
		}
	}
};

const createDefaultApollo = (
	httpLink: HttpLink,
	translationService: TranslationService
): ApolloClientOptions<NormalizedCacheObject> => ({
	link: createHttpLink(httpLink, translationService, apiUrlGql),
	cache: new InMemoryCache({ typePolicies })
});

const createNamedApollo = (httpLink: HttpLink, translationService: TranslationService): NamedOptions => ({
	staking: {
		link: createHttpLink(httpLink, translationService, apiUrlGqlStaking),
		cache: new InMemoryCache({ typePolicies })
	},
	bff: {
		link: createBffHttpLink(httpLink, translationService, apiUrlGqlBff),
		cache: new InMemoryCache({ typePolicies })
	}
});

@NgModule({
	imports: [ApolloModule],
	providers: [
		{
			provide: APOLLO_OPTIONS,
			useFactory: createDefaultApollo,
			deps: [HttpLink, TranslationService]
		},
		{
			provide: APOLLO_NAMED_OPTIONS,
			useFactory: createNamedApollo,
			deps: [HttpLink, TranslationService]
		}
	]
})
export class GraphQLModule {}
